import {Suspense, useContext, useEffect, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react/index.js'
import AtomButton, {atomButtonDesigns} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomImage from '@s-ui/react-atom-image'
import AtomTooltip, {AtomTooltipPlacements} from '@s-ui/react-atom-tooltip'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import MoleculeNotification, {
  moleculeNotificationAutoClose,
  VARIATIONS as moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import PrimitiveTypography, {
  primitiveTypographyDesign,
  primitiveTypographyLineHeight
} from '@s-ui/react-primitive-typography'
import {useLocation} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {
  getTrackingAccessOldChat,
  getTrackingCloseNet,
  getTrackingMenuClicked,
  getTrackingMessageSent,
  getTrackingNewConversation,
  getTrackingReviewDetailSent,
  useNetChat
} from '@adv-mt/mt-net-drawer'
import NetInput, {NET_INPUT_ID} from '@adv-mt/mt-net-input'
import NetMessageList from '@adv-mt/mt-net-message-list'
import NetSuggestions from '@adv-mt/mt-net-suggestions'
import NotificationsAdFavorite from '@adv-mt/mt-notifications-ad-favorite'
import IconChatConversationOutline from '@adv-mt/mt-svg-icons/lib/ChatConversationOutline.js'
import IconChevronRight from '@adv-mt/mt-svg-icons/lib/ChevronRight.js'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import IconLogo from '@adv-mt/mt-svg-icons/lib/Logo.js'
import IconShrink from '@adv-mt/mt-svg-icons/lib/Shrink.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../components/LinkFactory/index.js'
import {ROUTES} from '../../config/index.js'
import ChatsSidebar from './components/ChatsSidebar.js'
import ContactAdDrawer from './components/ContactAdDrawer.js'
import NetRestoringSkeleton from './components/NetRestoringSkeleton.js'
import ReviewDrawer from './components/ReviewDrawer.js'
import {getInitialProps} from './getInitialProps.js'

const BASE_CLASS = 'mt-Net'
const TERMS_CLASS = `${BASE_CLASS}-terms`

const NOTIFICATION_TYPES = {
  FEEDBACK_SUCCESS: 'feedback_success',
  NEW_CHAT_SUCCESS: 'new_chat_success'
}

const NOTIFICATION_COMPONENT_TYPE = {
  [NOTIFICATION_TYPES.FEEDBACK_SUCCESS]: 'success',
  [NOTIFICATION_TYPES.NEW_CHAT_SUCCESS]: 'success'
}

const Net = ({helmet, pageTrackingData, pageName: currentPageName = 'net'}) => {
  const {i18n, domain} = useContext(Context)
  const {page, track} = useAnalyticsTrack()
  const {query} = useLocation()

  const config = domain.get('config')
  const appName = config.get('APP_NAME')
  const urlStaticsImages = config.get('URL_STATICS_IMAGES')
  const {
    NET_LOGO_EXTENDED_URL: netLogoUrl,
    NET_SUGGESTION_TYPE,
    MESSAGE_TYPES,
    FEEDBACK_REVIEW_TAGS,
    DRAWER_REOPEN_SESSION_KEY,
    NET_INPUT_USER_DEFAULT_MESSAGE_SESSION_KEY,
    NET_ASSISTANT_PAGE_FULL_WIDTH_IPROMO
  } = config.get('COMMON').AI_ASSISTANT
  const isMobile = config.get('isMobile')
  const tracking = config.get('tracking')
  const {
    TRACKING_EVENT_NAMES: {
      NET_ASSISTANT_WELCOME_CTA_CLICKED,
      NET_FULL_SCREEN_EXIT_CLICKED,
      NET_ACCESS_HOME_CLICKED
    } = {}
  } = tracking || {}
  const TERMS_CLASS_LINK = `${TERMS_CLASS}-link`

  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })

  const isFullWidth = query?.ipromo === NET_ASSISTANT_PAGE_FULL_WIDTH_IPROMO
  const isComingFromDesktopDrawer = isHigherThanMobileResolution && isFullWidth

  // Map page name and context to use query ones or Net page current ones
  const pageName = query?.pageName || currentPageName
  const pageContext = {type: query?.pageType || NET_SUGGESTION_TYPE.HOME, adId: query?.adId}

  const {
    chatData,
    chats,
    restoring,
    retrying,
    isStorageReady,
    sendMessage,
    clearChat,
    loadChat,
    retry,
    onAdClick,
    onShowResults,
    onSearchSaved,
    onClickFavorite,
    onReviewSuccess,
    selectedReviewingBlock
  } = useNetChat({
    isOpen: true,
    i18n,
    pageName,
    isFullscreen: true,
    initialChatId: query?.chatId,
    context: {page: pageContext},
    onReviewNegative: () => setIsReviewDrawerOpen(true),
    onReviewPositive: () => setNotificationType(NOTIFICATION_TYPES.FEEDBACK_SUCCESS)
  })

  const [isContactDrawerOpen, setIsContactDrawerOpen] = useState(false)
  const [contactInfo, setContactInfo] = useState(null)
  const [userToggledSidebar, setUserToggledSidebar] = useState(null)
  const [isReviewDrawerOpen, setIsReviewDrawerOpen] = useState(false)
  const [notificationType, setNotificationType] = useState(null)
  const [isChatSelected, setIsChatSelected] = useState(false)

  const hasConversation = !!chatData?.chatId || !!chatData?.content?.length
  const isSidebarOpen =
    userToggledSidebar !== null
      ? userToggledSidebar
      : isStorageReady && isHigherThanMobileResolution && !!chats?.length && !isComingFromDesktopDrawer

  useMount(() => {
    pageTrackingData && page(pageTrackingData)
    if (query?.chatId) {
      setIsChatSelected(true)
    }
  })

  useEffect(() => {
    if (!chatData?.chatId) return
    const currentChatId = new URLSearchParams(window.location.search).get('chatId')
    if (currentChatId) return
    window.history.replaceState(null, '', `${ROUTES.NET}/?chatId=${chatData.chatId}`)
  }, [chatData.chatId])

  const onSearch = ({text, messageType}) => {
    setIsChatSelected(true)
    if (messageType === MESSAGE_TYPES?.PREDEFINED) {
      track({event: NET_ASSISTANT_WELCOME_CTA_CLICKED, data: {hit_information: text}})
    }
    sendMessage(text, messageType, {
      onTrack: (newChatData, messageData) => {
        track(getTrackingMessageSent(tracking, newChatData, messageData))
      }
    })
  }

  const onClickNewChat = () => {
    track(getTrackingNewConversation(tracking))
    setIsChatSelected(false)
    onConfirmNewChat()
  }

  const onConfirmNewChat = () => {
    clearChat()
    setNotificationType(NOTIFICATION_TYPES.NEW_CHAT_SUCCESS)
    window.history.replaceState(null, '', ROUTES.NET)
  }

  const onClickContact = contactInformation => {
    setUserToggledSidebar(false)
    setContactInfo(contactInformation)
    setIsContactDrawerOpen(true)
  }
  const onCloseContactDrawer = ad => {
    setContactInfo(null)
    setIsContactDrawerOpen(false)
  }

  const onSuccessReviewStep = (text, selectedTags = []) => {
    const tags = selectedTags.map(value => i18n.t(FEEDBACK_REVIEW_TAGS[value.toUpperCase()]?.label)).join(', ')
    track(getTrackingReviewDetailSent(tracking, {...selectedReviewingBlock, tags, text}))
    setIsReviewDrawerOpen(false)
    setNotificationType(NOTIFICATION_TYPES.FEEDBACK_SUCCESS)
  }

  const onToggleSidebar = newValue => {
    setUserToggledSidebar(newValue)
    if (newValue) {
      track(getTrackingMenuClicked(tracking, chatData?.chatId))
    }
  }

  const onClickToShrink = () => {
    const textarea = document.getElementById(NET_INPUT_ID)
    const currentInputValue = textarea?.value?.trim() || ''
    // Store the input value temporarily so the Net drawer input can retrieve it
    if (currentInputValue) {
      sessionStorage.setItem(NET_INPUT_USER_DEFAULT_MESSAGE_SESSION_KEY, currentInputValue)
    }

    track({event: NET_FULL_SCREEN_EXIT_CLICKED, data: {hit_information: chatData?.chatId}})

    // Use browser back instead of router goBack() to trigger proper bfcache restore
    // This will open the drawer when going back if the page uses useReopenDrawerOnSession
    window.history.back()
  }

  const onClickToClose = () => {
    sessionStorage.removeItem(DRAWER_REOPEN_SESSION_KEY)

    track(getTrackingCloseNet(tracking, chatData))
    // Use browser back instead of router goBack() to trigger proper bfcache restore
    // This will open the drawer when going back if the page uses useReopenDrawerOnSession
    window.history.back()
  }

  const onClickHome = () => {
    track({event: NET_ACCESS_HOME_CLICKED, data: {hit_information: chatData?.chatId}})
  }

  const netInputProps = {
    chatId: chatData?.chatId,
    onSubmit: (text, messageType) => {
      setIsChatSelected(true)
      sendMessage(text, messageType, {
        onTrack: (newChatData, messageData) => {
          track(getTrackingMessageSent(tracking, newChatData, messageData))
        }
      })
    },
    disabled: restoring || retrying || chatData.content?.some(message => message.loading)
  }

  const welcomeInputProps = {
    ...netInputProps,
    placeholder: isMobile
      ? [
          i18n.t('NET_PAGE.INPUT_PLACEHOLDER_1'),
          i18n.t('NET_PAGE.INPUT_PLACEHOLDER_2'),
          i18n.t('NET_PAGE.INPUT_PLACEHOLDER_3')
        ]
      : [
          i18n.t('NET_PAGE.INPUT_PLACEHOLDER_DESKTOP_1'),
          i18n.t('NET_PAGE.INPUT_PLACEHOLDER_DESKTOP_2'),
          i18n.t('NET_PAGE.INPUT_PLACEHOLDER_DESKTOP_3')
        ],
    animatedPlaceholder: true
  }

  const inputProps = hasConversation ? netInputProps : welcomeInputProps

  const pageClassName = cx(BASE_CLASS, {
    [`${BASE_CLASS}--conversation`]: hasConversation,
    [`${BASE_CLASS}--welcome`]: !hasConversation
  })

  if (!isStorageReady) return null

  return (
    <Layout.FullWidth linkFactory={LinkFactory} isFooterless isHeaderless>
      <ChatsSidebar
        isOpen={isSidebarOpen}
        setIsOpen={onToggleSidebar}
        isReady={isStorageReady}
        chats={chats}
        currentChatId={chatData?.chatId}
        onSelectChat={chatId => {
          track(getTrackingAccessOldChat(tracking, chatData?.chatId, chatId))
          setIsChatSelected(true)
          window.history.replaceState(null, '', `${ROUTES.NET}/?chatId=${chatId}`)
          loadChat(chatId)
        }}
        onNewChat={onClickNewChat}
        disabled={!hasConversation}
        isHigherThanMobileResolution={isHigherThanMobileResolution}
      />

      <main className={pageClassName}>
        <Helmet {...helmet} />
        <header className={`${BASE_CLASS}-header`}>
          <AtomImage
            src={`${urlStaticsImages}${netLogoUrl}`}
            className={cx(`${BASE_CLASS}-headerLogo`, {
              [`${BASE_CLASS}-headerLogo--hidden`]: isSidebarOpen
            })}
            alt="Net AI assistant"
          />
          <div className={`${BASE_CLASS}-headerActions`}>
            {!isHigherThanMobileResolution && (
              <AtomButton
                design={atomButtonDesigns.FLAT}
                aria-label={i18n.t('NET.SIDE_MENU.ARIA_LABEL_CHAT_HISTORY')}
                leftIcon={<IconChatConversationOutline size={ATOM_ICON_SIZES.medium} />}
                onClick={() => onToggleSidebar(true)}
              />
            )}
            {isComingFromDesktopDrawer && (
              <AtomButton
                design={atomButtonDesigns.FLAT}
                aria-label={i18n.t('NET.SHRINK')}
                leftIcon={<IconShrink size={ATOM_ICON_SIZES.medium} />}
                onClick={onClickToShrink}
              />
            )}
            {isComingFromDesktopDrawer ? (
              <AtomButton
                design={atomButtonDesigns.FLAT}
                aria-label={i18n.t('NET.CLOSE_CHAT')}
                leftIcon={<IconClose size={ATOM_ICON_SIZES.medium} />}
                onClick={onClickToClose}
              />
            ) : (
              <AtomTooltip
                placement={AtomTooltipPlacements.RIGHT}
                content={<span>{i18n.t(`NET_PAGE.BUTTON_TOOLTIP_${appName}`)}</span>}
              >
                <AtomButton
                  link
                  href={ROUTES.HOME}
                  onClick={onClickHome}
                  design={atomButtonDesigns.FLAT}
                  leftIcon={<IconLogo size={ATOM_ICON_SIZES.medium} />}
                  rightIcon={<IconChevronRight size={ATOM_ICON_SIZES.medium} />}
                  aria-label={i18n.t(`NET_PAGE.BUTTON_TOOLTIP_${appName}`)}
                >
                  {' '}
                  {/** Leave empty space to align left and right icons properly */}
                </AtomButton>
              </AtomTooltip>
            )}
          </div>
        </header>

        {restoring && (
          <section className={`${BASE_CLASS}-chat`}>
            <NetRestoringSkeleton />
          </section>
        )}

        {hasConversation && !restoring && (
          <section className={`${BASE_CLASS}-chat`}>
            <Suspense fallback={null}>
              <NetMessageList
                messages={chatData.content}
                chatId={chatData.chatId}
                onNewChat={onClickNewChat}
                onReviewSuccess={onReviewSuccess}
                onShowResults={onShowResults}
                onAdClick={onAdClick}
                onSearchSaved={onSearchSaved}
                onRetry={retry}
                retrying={retrying}
                restoring={restoring}
                onFavoriteChange={onClickFavorite}
                onClickContact={onClickContact}
                isWide
              />
            </Suspense>
          </section>
        )}

        {(!isChatSelected || (!hasConversation && !restoring)) && (
          <section className={`${BASE_CLASS}-welcome`}>
            <div className={`${BASE_CLASS}-welcomeText`}>
              {!isComingFromDesktopDrawer && (
                <PrimitiveTypography design={primitiveTypographyDesign.BODY_1}>
                  {i18n.t('NET_PAGE.WELCOME_SUBTITLE')}
                </PrimitiveTypography>
              )}
              <PrimitiveTypography
                className={`${BASE_CLASS}-welcomeHeadline`}
                design={primitiveTypographyDesign.DISPLAY_2}
                lineHeight={primitiveTypographyLineHeight.M}
              >
                {isComingFromDesktopDrawer ? i18n.t('NET.WELCOME.TITLE') : i18n.t('NET_PAGE.WELCOME_HEADLINE')}
              </PrimitiveTypography>
            </div>
            <div className={`${BASE_CLASS}-welcomeSuggestions`}>
              <NetSuggestions
                isCentered
                isOpenNetButtonVisible={false}
                onClickOption={onSearch}
                page={pageContext?.type}
                adId={pageContext?.adId}
              />
            </div>
          </section>
        )}

        <div className={`${BASE_CLASS}-bottom`}>
          <div className={`${BASE_CLASS}-input`}>
            <NetInput {...inputProps} />
          </div>

          <footer className={`${BASE_CLASS}-footer`}>
            <PrimitiveTypography design={primitiveTypographyDesign.CAPTION} className={TERMS_CLASS}>
              {htmlStringToReactElement(
                i18n.t('NET.TERMS', {
                  linkStartTerms: `<a class="${TERMS_CLASS_LINK}" href="/condiciones-de-uso/#18-contenido-generado-con-ia" target="_blank" rel="noopener">`,
                  linkEndTerms: '</a>',
                  linkStartPrivacy: `<a class="${TERMS_CLASS_LINK}" href="/condiciones-de-uso/#privacidad" target="_blank" rel="noopener">`,
                  linkEndPrivacy: '</a>'
                })
              )}
            </PrimitiveTypography>
          </footer>
        </div>
      </main>

      <ReviewDrawer
        isOpen={isReviewDrawerOpen}
        onSuccess={onSuccessReviewStep}
        onCancel={() => setIsReviewDrawerOpen(false)}
      />

      <ContactAdDrawer
        isOpen={isContactDrawerOpen}
        ad={contactInfo?.ad}
        message={contactInfo?.message}
        extendedTrackingData={contactInfo?.extendedTrackingData}
        onCancel={onCloseContactDrawer}
        pageName={pageName}
      />

      <NotificationsAdFavorite />

      {notificationType && (
        <div className={`${BASE_CLASS}-notification`}>
          <MoleculeNotification
            type={NOTIFICATION_COMPONENT_TYPE[notificationType]}
            show
            showCloseButton
            autoClose={moleculeNotificationAutoClose.medium}
            variation={moleculeNotificationVariations.negative}
            onClose={() => setNotificationType(null)}
          >
            {notificationType === NOTIFICATION_TYPES.FEEDBACK_SUCCESS && i18n.t('NET.FEEDBACK.NOTIFICATION_SUCCESS')}

            {notificationType === NOTIFICATION_TYPES.NEW_CHAT_SUCCESS && i18n.t('NET.NEW_CHAT.NOTIFICATION_SUCCESS')}
          </MoleculeNotification>
        </div>
      )}
    </Layout.FullWidth>
  )
}

Net.displayName = 'Net'

Net.propTypes = {
  helmet: PropTypes.object,
  pageTrackingData: PropTypes.object,
  pageName: PropTypes.string
}

Net.getInitialProps = getInitialProps

export default Net
